import { WindowHelper } from '@shared/helpers/window.helper';
import { Component, DestroyRef, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { SIDENAV_DATA } from '@shared/services/sidenav/sidenav.data';
import { ButtonSize, ButtonTypes } from '@shared/modules/ui/components/button/button.component';
import { SidenavEventType, SidenavService } from '@shared/services/sidenav/sidenav.service';
import {
  StickyFooterEvent,
  StickyFooterEventType,
  StickyFooterService
} from '@shared/services/sticky-footer.service';
import { ManagePanelService } from '@shared/services/manage-panel.service';
import { ProjectController } from '@modules/projects/shared/controllers/project.controller';
import { Project } from '@modules/projects/shared/models/project.model';
import { filter, switchMap, tap } from 'rxjs/operators';
import { ProjectAPIService } from '../../services/project-api.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProjectsTimelineSidenavTab, timelineSidenavTabs } from '../../consts/timeline-sidenav-tabs.const';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { Config } from '@shared/configs/config';
import { MatDialog } from '@angular/material/dialog';
import { ProjectPipelineAPIService } from '../../services/project-pipeline-api.service';
import { ProjectSnapshotService } from '../../services/project-snapshot.service';

@Component({
  styleUrls: ['./projects-timeline-sidenav.component.scss'],
  templateUrl: './projects-timeline-sidenav.component.html'
})
export class ProjectsTimelineSidenavComponent implements OnInit, OnDestroy {
  static componentName: string = 'ProjectTimelineSidenavComponent';

  public sideData = inject(SIDENAV_DATA);
  private readonly sideNavService = inject(SidenavService);
  private readonly footerService = inject(StickyFooterService);
  private readonly managePanelService = inject(ManagePanelService);
  private readonly projectService = inject(ProjectAPIService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly projectPipelineService = inject(ProjectPipelineAPIService);
  private readonly projectSnapshotService = inject(ProjectSnapshotService);

  WindowHelper = WindowHelper;
  ButtonSize = ButtonSize;

  project;

  isPreview$ = this.managePanelService.isPreview$;
  isFormDirty$ = this.managePanelService.isFormDirty$;

  protected activeElement = signal<string | null>(null);
  protected sidenavLoading = signal<boolean>(false);
  protected loading = signal<boolean>(false);
  protected isResourcesTimeline = signal<boolean>(false);
  protected timelineAction = signal<'add' | 'change' | null>(null);

  protected readonly tabs = timelineSidenavTabs;

  protected readonly footerButtons = [
    {
      action: () => this.cancel(),
      loading: false,
      type: ButtonTypes.NEW_SECONDARY_BLUE,
      text: 'Contacts.V2.cancel'
    },
    {
      action: () => this.save(),
      loading: true,
      type: ButtonTypes.NEW_PRIMARY_BLUE,
      text: 'Contacts.V2.save'
    }
  ];

  get selectedTabName(): string {
    return this.tabs.find((tab) => tab.isActive).name;
  }

  get selectedTabType(): string {
    return this.tabs.find((tab) => tab.isActive).type;
  }

  ngOnInit(): void {
    this.setInitialData();
    this.subscribeSavingState();
  }

  private setInitialData(): void {
    this.project = this.sideData?.data?.project;

    this.timelineAction.set(this.sideData.data.timelineAction);
    this.isResourcesTimeline.set(!!this.sideData.data.timelineId);
    if (this.isResourcesTimeline()) this.refreshProjectData();

    const tab = this.sideData?.data?.tab || 'range';
    if (this.sideData?.data?.elementId && tab === 'stage')
      this.activeElement.set(this.sideData.data.elementId);

    this.setTabActive(this.tabs.find(({ type }) => type === tab));
  }

  private refreshProjectData(): void {
    this.sidenavLoading.set(true);
    this.projectService
      .getProject(this.project.id)
      .pipe(
        tap((project: Project) => {
          const tab = this.sideData?.data?.tab;

          const overrides: Partial<Project['basicDataBox']> =
            {
              range: {
                termStart: this.project?.basicDataBox?.termStart,
                termEnd: this.project?.basicDataBox?.termEnd
              },
              pause: {
                pauseStart: this.project?.basicDataBox?.pauseStart,
                pauseEnd: this.project?.basicDataBox?.pauseEnd
              },
              stage: {
                activeTimelineStages:
                  this.project?.basicDataBox?.activeTimelineStages ??
                  project.basicDataBox?.activeTimelineStages
              }
            }[tab] ?? {};

          this.project = {
            ...project,
            basicDataBox: {
              ...project.basicDataBox,
              ...overrides
            }
          };

          this.sidenavLoading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private subscribeSavingState(): void {
    this.footerService.emitter
      .pipe(
        tap((event: StickyFooterEvent) => {
          switch (event.type) {
            case StickyFooterEventType.START_SAVING:
              this.loading.set(true);
              break;
            case StickyFooterEventType.END_SAVING:
              this.loading.set(false);
              break;
            default:
              break;
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  protected showProjectDetails(): void {
    const ctrl = new ProjectController(this.project);
    ctrl.preview(this.project).then(() => this.sideNavService.close());
  }

  protected setTabActive(selectedTab: { name: string; isActive: boolean }): void {
    this.tabs.forEach((tab) => (tab.isActive = false));
    selectedTab.isActive = true;
  }

  protected onTabChange(tabType: ProjectsTimelineSidenavTab): void {
    const tabIndex = this.tabs.findIndex((tab) => tab.type === tabType);
    this.setTabActive(this.tabs[tabIndex]);
  }

  protected save(): void {
    this.footerService.emitter.emit({ type: StickyFooterEventType.SUBMITTED });
  }

  protected cancel(): void {
    this.sideNavService.close();
  }

  protected removeProjectFromTimeline(): void {
    this.dialog
      .open(ConfirmModalComponent, {
        width: Config.CONFIRM_MODAL_WIDTH,
        autoFocus: false,
        data: {
          title: 'ProjectResources.projectRemove.title',
          body: 'ProjectResources.projectRemove.description',
          submitButtonType: ButtonTypes.NEW_RED,
          submitButtonIcon: './assets/img/ic-warning-v2.svg'
        }
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        filter(() => !!this.sideData?.data?.timelineId),
        switchMap(() =>
          this.projectPipelineService
            .removeProjectFromPipeline(this.sideData!.data!.timelineId, this.project!.id)
            .pipe(
              tap(() => {
                this.sideNavService.emitter.emit({
                  type: SidenavEventType.REFRESH,
                  data: true
                });
                this.cancel();
              })
            )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.projectSnapshotService.resetProjectPayloadsSnapshots();
  }
}
